import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { forwardRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Info } from '@/components/Icons/Info';
import { NewBubble } from '@/components/Icons/NewBubble';
import { NewCloseBubble } from '@/components/Icons/NewCloseBubble';
import { CustomIcon } from '@/components/Icons/NewCustomIcon';
import { TextReply } from '@/components/Icons/TextReply';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useController } from '@/hooks/useController';
import { useElementSize } from '@/hooks/useElementSize';
import { useWidgetState } from '@/hooks/useWidgetState';
import { OpeningMessage } from '@/newComponents/OpeningMessage/OpeningMessage';
import { isLeftAlignedSelector, isTopAlignedSelector, launcherSelector, showCloseIconSelector, themeSelector, bubbleImageSelector, webchatAriaLabelSelector, widgetPositionState, } from '@/state/config';
import { hasUnreadMessagesSelector, isWidgetOpenState } from '@/state/session';
export const Launcher = forwardRef((props, ref) => {
    const { ref: launcherRef, width, height } = useElementSize();
    const { t } = useTranslation();
    const { notifyWidgetResize } = useController();
    const { openWidget, minimizeWidget } = useWidgetState();
    const { trackEvent } = useAnalytics();
    const theme = useRecoilValue(themeSelector);
    const isOpen = useRecoilValue(isWidgetOpenState);
    const isLeft = useRecoilValue(isLeftAlignedSelector);
    const isTop = useRecoilValue(isTopAlignedSelector);
    const { size, type } = useRecoilValue(launcherSelector);
    const hasUnreadMessages = useRecoilValue(hasUnreadMessagesSelector);
    const { isLivechat } = useRecoilValue(widgetPositionState);
    const showCloseIcon = useRecoilValue(showCloseIconSelector);
    const bubbleImage = useRecoilValue(bubbleImageSelector);
    const ariaLabel = useRecoilValue(webchatAriaLabelSelector) || t('buttons.launcher_label');
    useEffect(() => {
        if (width && height && !isOpen) {
            notifyWidgetResize({
                width: `${width + 30}px`,
                height: `${height + 40}px`,
            });
        }
    }, [height, isOpen, notifyWidgetResize, width]);
    const handleClick = useCallback(() => {
        // Track launcher click event
        trackEvent(AnalyticsEvent.LauncherClicked);
        if (isOpen) {
            minimizeWidget();
        }
        else {
            openWidget();
        }
    }, [minimizeWidget, isOpen, openWidget, trackEvent]);
    const renderLauncher = useCallback(() => {
        if (isOpen && isLivechat) {
            if (!showCloseIcon) {
                return null;
            }
            return (_jsx(NewCloseBubble, { isTop: isTop, onClick: handleClick, className: "launcher-btn" /* TestClassName.LauncherButton */ }));
        }
        if (isOpen) {
            return null;
        }
        if (type === 'help' || type === 'feedback') {
            return (_jsxs("button", { className: cn("launcher-btn" /* TestClassName.LauncherButton */, 'rounded-(--moveo-border-radius-base) ml-auto flex w-fit cursor-pointer items-center border-0 px-3 py-2 shadow-xl', 'bg-(--color-launcher-background,var(--color-accent))', 'text-(--color-launcher-foreground,var(--color-background))'), tabIndex: 0, onClick: handleClick, "aria-label": ariaLabel, children: [type === 'help' ? _jsx(Info, {}) : _jsx(TextReply, {}), ' ', _jsx("p", { className: "m-0 ml-1 whitespace-nowrap", children: type === 'help' ? t('launchers.help') : t('launchers.feedback') })] }));
        }
        if (bubbleImage) {
            return (_jsx("div", { className: cn('flex justify-end'), children: _jsx(CustomIcon, { onClick: handleClick, theme: theme, size: size, imgUrl: bubbleImage, ariaLabel: ariaLabel }) }));
        }
        return (_jsx("div", { className: "flex justify-end", children: _jsx(NewBubble, { className: cn("launcher-btn" /* TestClassName.LauncherButton */, 'shadow-xl'), onClick: handleClick, size: size, "aria-label": ariaLabel }) }));
    }, [
        ariaLabel,
        bubbleImage,
        handleClick,
        isOpen,
        size,
        t,
        theme,
        type,
        isLivechat,
        isTop,
        showCloseIcon,
    ]);
    // Create a callback ref that merges both refs
    const setRefs = useCallback((node) => {
        // Set the forwarded ref
        if (typeof ref === 'function') {
            ref(node);
        }
        else if (ref) {
            ref.current = node;
        }
        // Set the launcherRef from useElementSize
        launcherRef.current = node;
    }, [ref, launcherRef]);
    // If the user clicks on the launcher before socket is connected,
    // the state will be back to Idle not loading the conversation.
    return (_jsxs("div", { ref: setRefs, className: cn('z-1100 pointer-events-auto absolute right-4 flex w-auto', isTop ? 'top-3 flex-col-reverse gap-1' : 'bottom-3 flex-col', {
            'left-4 right-auto': isLeft,
        }), children: [_jsx(OpeningMessage, {}), _jsxs("div", { className: cn('relative', isLeft ? 'mr-auto' : 'ml-auto'), children: [hasUnreadMessages && !isOpen && (_jsx("span", { className: "absolute right-0 top-0 z-10 h-2.5 w-2.5 rounded-full bg-red-500" })), _jsx("div", { className: "cursor-pointer transition-transform hover:scale-110 active:scale-95", children: renderLauncher() })] })] }));
});
Launcher.displayName = 'Launcher';
